/**
 * entitlements.js — Single source of truth for all role, tier, and feature access logic.
 *
 * Architecture:
 *   Universal Passport = base identity layer (all workers)
 *   Care Module = sector-specific add-on that sits ON TOP of Universal
 *   Future modules (education, hospitality, construction) follow the same pattern.
 *
 * This module mirrors backend/entitlements.py exactly.
 * All feature access decisions must go through canAccess().
 */

// ── Universal Passport — base identity layer ──
const UNIVERSAL_FEATURES = {
  identity_profile: true,
  certificate_upload: true,
  learning_modules: true,
  cross_sector_training: true,
  verified_identity: true,
  qr_passport: true,
};

// ── Care Module — sector-specific add-on ──
const CARE_MODULE_FEATURES = {
  care_training: true,
  care_certificates: true,
  medication_training: true,
  care_competencies: true,
  emar: true,
  care_plans: true,
  incident_reporting: true,
  safeguarding: true,
  dbs_integration: true,
  care_audit_evidence: true,
  auto_schedule: true,
  auto_assignment: true,
};

// ── Worker / Passport Entitlements ──
export const workerEntitlements = {
  free_worker: {
    identity_profile: true,
    certificate_upload: false,
    learning_modules: false,
    verified_identity: false,
    qr_passport: false,
  },
  carer_learner: {
    identity_profile: true,
    certificate_upload: true,
    learning_modules: true,
    care_training: true,
    verified_identity: false,
    qr_passport: true,
    auto_schedule: true,
    auto_assignment: true,
    emar: true,
    care_plans: true,
  },
  universal_passport: {
    // Base identity layer — all universal features, NO care module
    ...UNIVERSAL_FEATURES,
  },
  universal_plus_care_module: {
    // Universal + Care Module = full CareSkillset worker
    ...UNIVERSAL_FEATURES,
    ...CARE_MODULE_FEATURES,
  },
};

// ── Company Entitlements ──
export const companyEntitlements = {
  free: {
    passport_verification: true,
    certificate_upload: true,
  },
  growth: {
    passport_verification: true,
    care_training: true,
    care_certificates: true,
    medication_training: true,
    care_competencies: true,
    auto_schedule: true,
    auto_assignment: true,
    emar: true,
    care_plans: true,
    incident_reporting: true,
    safeguarding: true,
    dbs_integration: true,
    care_audit_evidence: true,
  },
  scale: {
    passport_verification: true,
    care_training: true,
    care_certificates: true,
    medication_training: true,
    care_competencies: true,
    auto_schedule: true,
    auto_assignment: true,
    emar: true,
    care_plans: true,
    incident_reporting: true,
    safeguarding: true,
    dbs_integration: true,
    care_audit_evidence: true,
    multi_branch: true,
    workforce_intelligence: true,
  },
  enterprise: {
    passport_verification: true,
    care_training: true,
    care_certificates: true,
    medication_training: true,
    care_competencies: true,
    auto_schedule: true,
    auto_assignment: true,
    emar: true,
    care_plans: true,
    incident_reporting: true,
    safeguarding: true,
    dbs_integration: true,
    care_audit_evidence: true,
    multi_branch: true,
    workforce_intelligence: true,
  },
};

// Regulator read-only features
const REGULATOR_FEATURES = ['passport_verification', 'workforce_intelligence', 'multi_branch'];

/**
 * Universal access checker — the ONLY place where entitlement logic is implemented.
 *
 * Architecture:
 *   - Universal Passport is the base identity layer
 *   - Care Module is a sector add-on that sits on top of Universal
 *   - universal_plus_care_module = Universal + Care Module (composited)
 */
export function canAccess({ role, companyTier, workerTier, feature }) {
  if (role === 'super_admin') return true;

  if (role === 'regulator') {
    return REGULATOR_FEATURES.includes(feature);
  }

  // Company admin / staff
  if (role === 'company_admin' || role === 'company_staff') {
    if (!companyTier) return false;
    const tierMap = companyEntitlements[companyTier.toLowerCase()] || {};
    return !!tierMap[feature];
  }

  // Worker roles
  if (['carer_learner', 'worker', 'learner', 'care_worker'].includes(role)) {
    if (!workerTier) return false;
    const tierMap = workerEntitlements[workerTier.toLowerCase()] || {};
    return !!tierMap[feature];
  }

  return false;
}

/**
 * Check if a worker tier includes the Care Module add-on.
 */
export function hasCareModule(workerTier) {
  return workerTier === 'universal_plus_care_module' || workerTier === 'carer_learner';
}

/**
 * Route-to-feature mapping for FeatureRoute guards.
 */
export const ROUTE_FEATURE_MAP = {
  '/emar': 'emar',
  '/emar/history': 'emar',
  '/care-plan': 'care_plans',
  '/care-management': 'care_plans',
  '/schedule': 'auto_schedule',
  '/incidents': 'incident_reporting',
  '/incidents/new': 'incident_reporting',
  '/multi-branch': 'multi_branch',
  '/intelligence': 'workforce_intelligence',
  '/staff-competency': 'care_training',
  '/competency-templates': 'care_training',
  '/competency-sessions': 'care_training',
  '/compliance': 'safeguarding',
  '/safeguarding': 'safeguarding',
  '/shadowing': 'care_training',
  '/passport/upload': 'certificate_upload',
  '/certificates/verify': 'passport_verification',
  '/passport-requests': 'passport_verification',
  '/registry-connectors': 'passport_verification',
  '/passport-transfers': 'passport_verification',
};

/**
 * Human-readable labels for feature keys.
 */
export const FEATURE_LABELS = {
  identity_profile: 'Identity Profile',
  certificate_upload: 'Certificate Upload',
  learning_modules: 'Learning Modules',
  care_training: 'Care Training',
  care_certificates: 'Care Certificates',
  medication_training: 'Medication Training',
  care_competencies: 'Care Competencies',
  cross_sector_training: 'Cross-Sector Training',
  verified_identity: 'Verified Identity',
  qr_passport: 'QR Passport',
  auto_schedule: 'Auto Scheduling',
  auto_assignment: 'Auto Assignment',
  emar: 'eMAR',
  care_plans: 'Care Plans',
  incident_reporting: 'Incident Reporting',
  safeguarding: 'Safeguarding',
  dbs_integration: 'DBS Integration',
  care_audit_evidence: 'Care Audit Evidence',
  multi_branch: 'Multi-Branch',
  workforce_intelligence: 'Workforce Intelligence',
  passport_verification: 'Passport Verification',
};

/**
 * Pricing products — two separate products, not a single tier.
 */
export const WORKER_PRODUCTS = {
  universal_passport: {
    name: 'Universal Passport',
    description: 'Verified identity, cross-sector competencies, QR identity, document vault, multi-industry portability',
    isBase: true,
  },
  care_module: {
    name: 'CareSkillset Module',
    description: 'Care certificates, mandatory training, medication training, care competencies, CQC compliance, DBS integration',
    isAddOn: true,
    requiresBase: 'universal_passport',
  },
};
